
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { MdePopoverTrigger } from '@material-extended/mde';
import { ServiceWorkerService } from 'fitforce-offline-first';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { User } from './classes/user';
import { AlertDialogComponent } from './components/alert-dialog/alert-dialog.component';
import { ChangePasswordDialogComponent } from './components/change-password-dialog/change-password-dialog.component';
import { InitializeLoadScreenComponent } from './components/initialize-load-screen/initialize-load-screen.component';
import { ExerciseEditorGuard } from './exercise-program/services/exercise-editor.guard';
import { AuthService } from './services/auth.service';
import { OfflineReason, OfflineService } from './services/offline.service';

declare let gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  version: string = environment.version;
  isOnline: boolean;
  navigatorOnline: boolean;
  inProd: boolean = environment.production;
  username: string;
  offlineStatusSubscription: Subscription = new Subscription();
  currentUserSubscription: Subscription = new Subscription();
  currentUser: User;
  backgroundImg = 0;

  @ViewChild(MdePopoverTrigger, { static: true }) offlineTabTrigger: MdePopoverTrigger;

  constructor(
    public authService: AuthService,
    public offline: OfflineService,
    private router: Router,
    private dialog: MatDialog,
    private deviceService: DeviceDetectorService,
    private exerciseEditorGuard: ExerciseEditorGuard,
    serviceWorkerService: ServiceWorkerService
  ) {
    serviceWorkerService.init(environment.checkUpdateInterval);
    serviceWorkerService.checkForUpdate();
  }

  ngOnInit() {
    this.backgroundImg = 1;
    this.offline.pingServer();

    this.isOnline = this.offline.isOnline();
    this.offlineStatusSubscription = this.offline.status.subscribe(value => {
      this.isOnline = value;
      this.navigatorOnline = this.offline.isNavigatorOnline();
    });

    this.currentUserSubscription = this.authService.currentUserObs.subscribe((user: User) => {
      if (!user) {
        this.offline.clearUserDatabase().subscribe();
        this.currentUser = null;
      } else {
        this.currentUser = user;
      }
    });

    this.authService.monitorSession().subscribe();

    // Check browser, if incompatible send warning.
    const devInfo = this.deviceService.getDeviceInfo();
    const browser = devInfo.browser;
    if (this.deviceService.isMobile()) {
      this.dialog.open(AlertDialogComponent, <MatDialogConfig> {
        data: {
          title: 'Mobile Device Detected',
          content: 'Looking for USMC Workout of the Day? Head to FitForce Mobile to start working out!',
          redirectUrl: this.getMobileLink(),
          button: 'Launch Mobile',
          cancelButton: 'Close',
        }
      });
    } else if (browser !== 'Chrome' && browser !== 'MS-Edge' && browser !== 'Firefox') {
      this.dialog.open(AlertDialogComponent, <MatDialogConfig> {
        data: {
          title: 'WARNING: Unsupported Browser!',
          content: 'This application does not support this browser, '
            + 'you may have performance or usability issues.\n'
            + ' Please use one of these supported browsers:',
          list : ['Google Chrome', 'Microsoft Edge', 'Mozilla Firefox'],
          button : 'Continue Anyway'
        }
      });
    }
  }

  ngOnDestroy() {
    this.offlineStatusSubscription.unsubscribe();
    this.currentUserSubscription.unsubscribe();
  }

  openSyncStatus() {
    this.dialog.open(InitializeLoadScreenComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: false,
      closeOnNavigation: true,
      data: {
        sync: false,
        autoClose: false,
        label: false,
        buttonText: 'Close',
        downloadingState: false,
        progressBar: false,
      },
    });
  }

  /**
   * Open the modal to change the password of the account. Display the right message after the save
   * occurs.
   */
  changePassword() {
    this.dialog.open(ChangePasswordDialogComponent, <MatDialogConfig> {
      width: '400px'
    }).afterClosed().subscribe(result => {
      if (result) {
        this.authService.changePassword(result.current, result.newPW)
          .subscribe((response: any) => {
            if (response['status'].indexOf('success') > -1) {
              this.dialog.open(AlertDialogComponent, <MatDialogConfig> {
                data: {
                  title: 'Success',
                  content: 'Password successfully changed.'
                }
              });
            }
          }, () => {
            this.dialog.open(AlertDialogComponent, <MatDialogConfig> {
              data: {
                title: 'Unsuccessful',
                content: 'The original password is incorrect.'
              }
            });
          });
      }
    });
  }

  /**
   * Check if the given route name is the current route.
   *
   * @param  {string} name - name of route to check
   */
  isActive(name: string) {
    return this.router.url.indexOf(name) > -1;
  }

  /**
   * Determine when to show which nav tabs to display depending if the user is logged in and the route of the page.
   */
  loggedIn() {
    return this.authService.isLoggedIn && this.router.url.indexOf('login') < 0 && this.router.url !== '/';
  }

  isAdmin() {
    return this.currentUser ? this.currentUser.isAdmin : false;
  }

  canEditExercises() {
    return this.currentUser ? this.exerciseEditorGuard.canEdit(this.currentUser) : false;
  }

  /**
   * Call logout on the auth service.
   *
   * @param  {} loggedIn - boolean to check if user is currently logged in
   */
  logout(loggedIn) {
    if (loggedIn) {
      this.authService.logout();
    }
  }

  showPopover() {

    if (!this.isOnline) {
      this.offlineTabTrigger.togglePopover();
    }
  }

  forceToggleOffline() {
    this.offline.forceOffline(this.isOnline, OfflineReason.FORCED);

    if (this.offline.isOnline()) {
      this.dialog.open(InitializeLoadScreenComponent, {
        width: '500px',
        maxWidth: '90vw',
        disableClose: true,
        closeOnNavigation: false,
        data: {
          sync: true,
          autoClose: true,
        },
      });
    }
  }

  getMobileLink() {
    return environment.mobileUrl === '{{MOBILE_URL}}' ?  `https://${window.location.host}/mobile` : environment.mobileUrl;
  }
}
